import {
  ICustomDateFormatConfig,
  ICustomNumberFormatConfig,
  IFormatterDataModel,
} from '../../data-models/formatter.data-model';
import { FMT } from '../../util/formatter-service';

/**
 * Display metadata for a formatter, for pickers that have to show a human which formatter
 * to choose. `displayName` and `description` are authored in Config > Formatters and reach
 * the app on the tenant's formatter rows; the names here fill the gap for ids that were
 * never curated, and the rest derives the facts (family, decimals, sample output) that are
 * always true of the config and so never worth writing by hand.
 */

/** Family labels, in the order they should appear in a grouped list. */
export const FormatterFamily = {
  currency: 'Currency',
  percentage: 'Percentage',
  multiple: 'Multiple',
  number: 'Number',
  date: 'Date',
  yearMonth: 'Year & month',
  time: 'Time',
  other: 'Other',
} as const;

const FAMILY_ORDER: string[] = [
  FormatterFamily.currency,
  FormatterFamily.percentage,
  FormatterFamily.multiple,
  FormatterFamily.number,
  FormatterFamily.date,
  FormatterFamily.yearMonth,
  FormatterFamily.time,
  FormatterFamily.other,
];

/**
 * Names for the code-defined formatters. A tenant row's `displayName` wins over these —
 * see {@link getFormatterDisplayName}.
 */
const FORMATTER_NAMES: Record<string, string> = {
  // Number
  currency: 'Currency',
  currency0Dp: 'Currency, no decimals',
  currencyShort: 'Currency, compact',
  currencyShort2dp: 'Currency, compact with decimals',
  usd: 'US Dollars',
  usdShort: 'US Dollars, compact',
  percent: 'Percentage',
  percent0Dp: 'Percentage, no decimals',
  percent2DpShort: 'Percentage, compact',
  percentHighPrecision: 'Percentage, high precision',
  decimalPercent: 'Percentage from a fraction',
  multiplier: 'Multiple',
  multiplierHighPrecision: 'Multiple, high precision',
  numeric: 'Number',
  numeric1DP: 'Number, 1 decimal',
  numeric2DP: 'Number, 2 decimals',
  numericShort: 'Number, compact',
  integer: 'Whole number',
  naturalNumber: 'Whole number, no negatives',
  entityInteger: 'Whole number (Entity Management)',
  entityPercent: 'Percentage (Entity Management)',
  entityMultiple: 'Multiple (Entity Management)',

  // Date
  date: 'Date',
  dateLocal: 'Date, local time zone',
  dateNumeric: 'Date, numeric',
  dateShort: 'Date, no year',
  dateTime: 'Date and time',
  dayMonth: 'Day and month',
  monthLong: 'Month',
  monthYear: 'Month and year',
  monthYearLong: 'Month and year, long',
  YYYY: 'Year',
  // The `YY` config is registered under a lowercase id; key both so either resolves.
  YY: 'Year, 2-digit',
  yy: 'Year, 2-digit',
  time: 'Time',
  time12hr: 'Time, 12-hour',
  timeLocal: 'Time, local time zone',

  // Boolean
  yesNo: 'Yes / No',
  trueFalse: 'True / False',

  // String
  string: 'Plain text',
  capitalizedString: 'Capitalized',
  camelCaseToCapitalizedString: 'camelCase to words',
  entityText: 'Text (Entity Management)',

  // Array
  stringArray: 'Text list',
  numberArray: 'Number list',
  percentArray: 'Percentage list',
  usdArray: 'Currency list',
};

// Noon UTC: a local-midnight sample renders as the previous day under `timeZone: 'UTC'`,
// which most of the seeded date formatters use.
export const SAMPLE_DATE = new Date('2025-01-01T12:00:00Z');

export function getFormatterDisplayName(model: IFormatterDataModel<unknown>): string {
  // The backfill migration defaults displayName to the id, so a row nobody has curated
  // carries no more than the id does — fall through to the curated name in that case.
  if (model.displayName && model.displayName !== model.id) {
    return model.displayName;
  }
  return FORMATTER_NAMES[model.id] ?? humanizeId(model.id);
}

function humanizeId(id: string): string {
  const words = id
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The sub-family an option belongs to, derived from the config rather than a list of ids,
 * so a tenant's own formatters land in the right group without any registration.
 *
 * Returns `''` for the types whose whole list is short enough to read flat (string,
 * boolean, array) — the picker suppresses group headings when only one group results.
 */
export function getFormatterFamily(model: IFormatterDataModel<unknown>): string {
  if (model.type === 'number' || model.type === 'currency') {
    const config = (model.config ?? {}) as ICustomNumberFormatConfig;
    if (config.style === 'currency') {
      return FormatterFamily.currency;
    }
    if (config.style === 'percent' || config.suffix === '%') {
      return FormatterFamily.percentage;
    }
    if (config.suffix === 'x') {
      return FormatterFamily.multiple;
    }
    return config.suffix ? FormatterFamily.other : FormatterFamily.number;
  }

  if (model.type === 'date') {
    const config = (model.config ?? {}) as ICustomDateFormatConfig;
    const hasDate = config.dateStyle != null || config.day != null;
    if (!hasDate && (config.timeStyle != null || config.hour != null)) {
      return FormatterFamily.time;
    }
    return hasDate ? FormatterFamily.date : FormatterFamily.yearMonth;
  }

  return '';
}

/** Sorts by family in {@link FAMILY_ORDER}, then by name. */
export function compareFormatterFamilies(a: string, b: string): number {
  const orderA = FAMILY_ORDER.indexOf(a);
  const orderB = FAMILY_ORDER.indexOf(b);
  return (orderA === -1 ? FAMILY_ORDER.length : orderA) - (orderB === -1 ? FAMILY_ORDER.length : orderB);
}

/**
 * A rendered sample, as `positive / negative / round`. The inputs vary by family so the
 * output is representative: a percentage formatter fed a four-figure sample reads as
 * broken, and a compact one fed a four-figure sample never gets to show its abbreviation.
 */
export function getFormatterExample(formatterModel: IFormatterDataModel<unknown>): string {
  if (formatterModel.type === 'date') {
    return FMT.format(formatterModel, SAMPLE_DATE);
  }
  if (formatterModel.type === 'number' || formatterModel.type === 'currency') {
    const formatter = FMT.get().getFormatterForModel(formatterModel);
    const [sample, round] = getNumberSamples(formatterModel);
    return `${formatter(sample)} / ${formatter(-sample)} / ${formatter(round)}`;
  }
  if (formatterModel.type === 'array') {
    return FMT.get().getFormatterForModel(formatterModel)(['Value 1', 'Value 2']);
  }
  if (formatterModel.type === 'boolean') {
    return FMT.get().getFormatterForModel(formatterModel)(true);
  }
  return FMT.get().getFormatterForModel(formatterModel)('Value');
}

function getNumberSamples(model: IFormatterDataModel<unknown>): [sample: number, round: number] {
  const config = (model.config ?? {}) as ICustomNumberFormatConfig;
  const family = getFormatterFamily(model);

  if (family === FormatterFamily.percentage) {
    // `style: 'percent'` multiplies by 100, so a fraction is its representative input;
    // the suffix-only percent formatters take the percentage itself.
    return config.style === 'percent' ? [0.123456, 1] : [12.3456, 100];
  }
  if (family === FormatterFamily.multiple) {
    return [2.5678, 3];
  }
  if (config.notation === 'compact') {
    return [1_234_567.89, 1_000_000];
  }
  return [1234.123456789, 1000];
}

/**
 * The second line of an option: what the formatter is for, and how precise it is.
 *
 * Only the description authored in Config > Formatters, never a stand-in for it — anything
 * this file could invent restates the name or the sample already sitting beside it. An
 * uncurated formatter is better off showing just its precision than a sentence of nothing.
 */
export function getFormatterHint(model: IFormatterDataModel<unknown>): string | undefined {
  const description = model.description?.trim();
  const decimals = getDecimalsHint(model);

  if (!description) {
    return decimals;
  }
  // Many authored descriptions are themselves the precision — several read exactly
  // "0 decimals" — so appending the derived one would print it twice.
  if (!decimals || description.toLowerCase().includes(decimals.toLowerCase())) {
    return description;
  }
  return `${description} · ${decimals}`;
}

function getDecimalsHint(model: IFormatterDataModel<unknown>): string | undefined {
  if (model.type !== 'number' && model.type !== 'currency') {
    return undefined;
  }
  const config = (model.config ?? {}) as ICustomNumberFormatConfig;
  if (config.notation === 'compact') {
    // Compact notation resolves to 0 fraction digits whatever the config asks for, so a
    // decimal count here would contradict the sample sitting next to it.
    return 'compact';
  }
  const { minimumFractionDigits: min, maximumFractionDigits: max } = new Intl.NumberFormat(
    navigator.language,
    config
  ).resolvedOptions();
  if (min == null || max == null) {
    return undefined;
  }
  if (min === max) {
    return `${max} decimal${max === 1 ? '' : 's'}`;
  }
  return `${min}-${max} decimals`;
}
